import { Autocomplete, TextField, Button, IconButton, InputAdornment } from "@mui/material";
import { Grid } from "@mui/material";
import React, { useContext, useMemo, useState } from "react";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import { ProjectContext } from "../../../Contexts/Project/Context";
import { useQuery } from "@apollo/client/react";
import CurrentUserContext from "@/Contexts/CurrentUser";
import PermissionContext from "@/Contexts/Permissions";
import { PermissionActionEnum, PermissionEntityEnum, GetClientsBasicDocument } from "@/graphql/generated/graphql";
import { isClientRepComplete, isValidEmail } from "./validation";
import EssealPhoneInput from "@/components/EssealPhoneInput";
import { objectLength } from "@/utils/helperFunctions";

const ADD_NEW_CLIENT_OPTION = { id: "0", name: "Add New Client" };

type RepField = "name" | "email" | "phone";

const ClientBasics: React.FC = () => {
  const { currentUser } = useContext(CurrentUserContext);
  const { canPerformAction } = useContext(PermissionContext);
  const { Create } = PermissionActionEnum;
  const { Clients } = PermissionEntityEnum;
  const { parentProject, project, onProjectDataChange, onClientRepChange, onClientRepRemove, onAddClientRep } = useContext(ProjectContext);
  const { data: clients, loading: clientsLoading } = useQuery(GetClientsBasicDocument);

  // Show validation errors only after the user has interacted with a field
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (index: number, field: RepField) =>
    setTouched((prev) => ({ ...prev, [`${index}-${field}`]: true }));
  const isTouched = (index: number, field: RepField) => Boolean(touched[`${index}-${field}`]);

  const clientOptions = useMemo(() => {
    const clientsList = clients?.getClients || [];

    if (currentUser?.isOwner || canPerformAction(Create, Clients)) {
      return [ADD_NEW_CLIENT_OPTION, ...clientsList];
    }

    return [...clientsList];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients?.getClients, currentUser?.isOwner]);

  const selectedClient = useMemo(() => {
    if (!project.clientId) return null;

    return (
      clientOptions.find((option) => option.id === project.clientId) ??
      (parentProject?.client?.id === project.clientId ? parentProject.client : null)
    );
  }, [clientOptions, project.clientId, parentProject]);

  const allRepsComplete = useMemo(() => {
    if (!project.clientReps || project.clientReps.length === 0) return true;
    return project.clientReps.every(isClientRepComplete);
  }, [project.clientReps]);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Autocomplete
          id="client-selector"
          options={clientOptions}
          loading={clientsLoading}
          autoHighlight
          fullWidth
          value={selectedClient}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => onProjectDataChange("clientId", newValue?.id ?? "")}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client (Optional)"
              slotProps={{
                htmlInput: {
                  ...params.inputProps,
                  autoComplete: "off",
                },
              }}
            />
          )}
        />
      </Grid>
      {project.clientId && (
        <>
          {project.clientReps && project.clientReps.length > 0 && (
            <Grid size={12}>
              <Grid container spacing={2}>
                {project.clientReps.map((rep, index) => {
                  const nameError = isTouched(index, "name") && !rep.name;
                  const emailError = Boolean(rep.email) && !isValidEmail(rep.email);
                  return (
                    <Grid size={12} key={`client-rep-${index}`}>
                      <Grid container spacing={1} alignItems="flex-start">
                        <Grid size={11}>
                          <Grid container spacing={2}>
                            <Grid size={12}>
                              <TextField
                                autoComplete="off"
                                fullWidth
                                id={`rep-name-${index}`}
                                label="Principal Contact"
                                name="rep-name"
                                value={rep.name || ''}
                                onChange={(e) => onClientRepChange(index, "name", e.target.value)}
                                onBlur={() => markTouched(index, "name")}
                                error={nameError}
                                helperText={nameError ? 'Principal contact is required' : ''}
                                slotProps={{ input: { sx: { backgroundColor: "background.paper" } } }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <TextField
                                autoComplete="off"
                                fullWidth
                                id={`rep-email-${index}`}
                                label="Email (Optional)"
                                name="rep-email"
                                type="email"
                                value={rep.email || ''}
                                onChange={(e) => onClientRepChange(index, "email", e.target.value)}
                                error={emailError}
                                helperText={emailError ? 'Enter a valid email address' : ''}
                                slotProps={{
                                  input: {
                                    sx: { backgroundColor: "background.paper" },
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <EmailIcon fontSize="small" />
                                      </InputAdornment>
                                    ),
                                  }
                                }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                              <EssealPhoneInput
                                defaultCountry="GB"
                                defaultValue={rep.phone}
                                size="lg"
                                onChange={(e164) => {
                                  onClientRepChange(index, "phone", e164)
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid size={1} display="flex" justifyContent="center" alignItems="flex-start" pt={1}>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Remove contact ${rep.name || index + 1}`}
                            onClick={() => onClientRepRemove(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}

          <Grid size={12}>
            <Button
              variant="outlined"
              fullWidth
              onClick={onAddClientRep}
              disabled={!allRepsComplete}
              sx={{
                mt: 2,
              }}
            >
              Add {objectLength(project.clientReps) ? "Another" : "A"} Principal Contact
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default ClientBasics;
